/**
 * ConfirmDialog — popup de confirmação reutilizável (substitui window.confirm).
 *
 * Props:
 *   open        — controla visibilidade
 *   title       — título curto
 *   message     — corpo (string ou nós); pode conter <strong>/quebras
 *   confirmLabel/cancelLabel — rótulos dos botões
 *   tone        — 'primary' (padrão) | 'danger'
 *   busy        — desabilita o confirmar enquanto processa
 *   onConfirm/onCancel
 *
 * a11y: role="dialog", aria-modal, foco no confirmar ao abrir, Tab preso no
 * diálogo, Esc cancela e o foco VOLTA ao elemento que abriu o diálogo.
 */
import { useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { C } from '@/theme/palette';
import { Btn } from './Btn';

const FOCAVEIS = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar',
  tone = 'primary', busy = false, onConfirm, onCancel,
}) {
  const confirmRef = useRef(null);
  const panelRef = useRef(null);
  const retornoRef = useRef(null);

  // Guarda quem tinha o foco ao abrir e devolve ao fechar (retorno de foco).
  useEffect(() => {
    if (!open) return undefined;
    retornoRef.current = document.activeElement;
    confirmRef.current?.focus();
    return () => {
      const el = retornoRef.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) { onCancel?.(); return; }
      if (e.key === 'Tab' && panelRef.current) {
        const itens = [...panelRef.current.querySelectorAll(FOCAVEIS)];
        if (!itens.length) return;
        const primeiro = itens[0];
        const ultimo = itens[itens.length - 1];
        if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
        else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  const accent = tone === 'danger' ? C.hot : C.cyber;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: `${C.bgDeep}C4`, backdropFilter: 'blur(5px)',
      }}
    >
      <div
        ref={panelRef}
        style={{
          width: '100%', maxWidth: 520, background: C.blackElev,
          backgroundImage: `linear-gradient(135deg, ${C.blackElev2}, ${C.blackElev})`,
          border: `1px solid ${C.blackBorder}`, borderRadius: 16,
          padding: 24, boxShadow: `0 25px 80px ${C.bgDeep}CC`, maxHeight: 'calc(100dvh - 32px)',
          overflow: 'auto',
        }}
      >
        <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
          <AlertTriangle size={18} style={{ color: accent }} aria-hidden="true" />
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 650, color: C.neutral, flex: 1 }}>{title}</h3>
          <button
            onClick={() => !busy && onCancel?.()}
            aria-label="Fechar" title="Fechar"
            style={{
              background: 'none', border: `1px solid ${C.blackBorder}`, color: C.neutralDim,
              cursor: 'pointer', width: 32, height: 32, borderRadius: 8, display: 'grid', placeItems: 'center',
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div style={{ fontSize: 14, color: C.neutralDim, lineHeight: 1.6, whiteSpace: 'pre-line', marginBottom: 20 }}>
          {message}
        </div>
        <div className="flex items-center justify-end gap-2" style={{ flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="md" onClick={() => !busy && onCancel?.()}>
            {cancelLabel}
          </Btn>
          <button
            ref={confirmRef}
            onClick={() => !busy && onConfirm?.()}
            disabled={busy}
            style={{
              background: accent, color: C.black, border: `1px solid ${accent}`, borderRadius: 8,
              minHeight: 40, padding: '0 18px', fontSize: 14, fontWeight: 650,
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Processando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
